// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    vec2,
    vec3,
} from 'gl-matrix';
import { dicomDosePixelDataToInternal } from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { Modality } from './modality';

// DICOM Dose Units Attribute (3004,0002)
export enum DoseUnit {
    GY = 'GY',
    RELATIVE = 'RELATIVE',
}

// DICOM Dose Type Attribute (3004,0004)
export enum DoseType {
    PHYSICAL = 'PHYSICAL',
    EFFECTIVE = 'EFFECTIVE',
    ERROR = 'ERROR',
}

// DICOM Dose Summation Type Attribute (3004,000a)
export enum DoseSummationType {
    PLAN = 'PLAN',
    MULTI_PLAN = 'MULTI_PLAN',
    FRACTION = 'FRACTION',
    BEAM = 'BEAM',
    BRACHY = 'BRACHY',
    FRACTION_SESSION = 'FRACTION_SESSION',
    BEAM_SESSION = 'BEAM_SESSION',
    BRACHY_SESSION = 'BRACHY_SESSION',
    CONTROL_POINT = 'CONTROL_POINT',
    RECORD = 'RECORD',
}

// DICOM DVH Type Attribute (3004,0001)
export enum DVHType {
    DIFFERENTIAL = 'DIFFERENTIAL',
    CUMULATIVE = 'CUMULATIVE',
    NATURAL = 'NATURAL',
}

// DICOM DVH Volume Units Attribute (3004,0054)
export enum DVHVolumeUnits {
    CM3 = 'CM3',
    PERCENT = 'PERCENT',
    PER_U = 'PER_U',
}

export class DicomDvh {
    public readonly doseUnits: DoseUnit;
    public readonly doseType: DoseType;
    public readonly dvhType: DVHType;
    public readonly dvhVolumeUnits: DVHVolumeUnits;
    public readonly dvhNumberOfBins: number;
    public readonly dvhDoseScaling: number;
    public readonly dvhData: number[];
    public readonly referencedRoiNumber: number;
    public readonly dvhMinimumDose: number;
    public readonly dvhMeanDose: number;
    public readonly dvhMaximumDose: number;

    constructor(
        doseUnits: DoseUnit,
        doseType: DoseType,
        dvhType: DVHType,
        dvhVolumeUnits: DVHVolumeUnits,
        dvhNumberOfBins: number,
        dvhdosescaling: number,
        dvhData: number[],
        referencedRoiNumber: number,
        dvhMinimumDose: number,
        dvhMeanDose: number,
        dvhMaximumDose: number
    ) {
        this.doseUnits = doseUnits;
        this.doseType = doseType;
        this.dvhType = dvhType;
        this.dvhVolumeUnits = dvhVolumeUnits;
        this.dvhNumberOfBins = dvhNumberOfBins;
        this.dvhDoseScaling = dvhdosescaling;
        this.dvhData = dvhData;
        this.referencedRoiNumber = referencedRoiNumber;
        this.dvhMinimumDose = dvhMinimumDose;
        this.dvhMeanDose = dvhMeanDose;
        this.dvhMaximumDose = dvhMaximumDose;
    }
}

export class DicomDose extends DicomBase {
    /**
     * Physical distance in between the center of each pixel, specified by a numeric pair.\
     * The pixelSpacing in planar directions should stay constant between slices.
     * (0028,0030) https://dicom.innolitics.com/ciods/rt-dose/image-plane/00280030
     */
    public readonly pixelSpacing: ReadonlyVec2;

    /**
     * Separation between slices in mm.
     * Determined based on the GridFrameOffsetVector attribute.
     * (3004,000C) https://dicom.innolitics.com/ciods/rt-dose/rt-dose/3004000c
     */
    public readonly sliceResolution: number;

    /**
     * The x, y, and z coordinates of the upper left hand corner (center of the first voxel transmitted) of the image, in mm.\
     * These are the PatientSpace coordinates.\
     * Note the coordinates are for the CENTER of the first voxel.\
     * So half a pixel offset is often needed to get more meaningful coordinates.
     * (0020,0032) https://dicom.innolitics.com/ciods/rt-dose/image-plane/00200032
     */
    public readonly imagePosition: ReadonlyVec3;

    /**
     * This is the rotation of the Image in PatientSpace.\
     * By applying this matrix to a vertex, it can be converted from ImageSpace to PatientSpace.
     * (0020,0037) https://dicom.innolitics.com/ciods/rt-dose/image-plane/00200037
     */
    public readonly imageOrientation: ReadonlyMat3;

    public readonly seriesDescription: string;

    /**
     * (0008,0021) Series Date - date the series started.
     * Format: YYYYMMDD per DICOM DA VR.
     */
    public readonly seriesDate?: string;

    /**
     * (0008,0031) Series Time - time the series started.
     * Format: HHMMSS.FFFFFF per DICOM TM VR.
     */
    public readonly seriesTime?: string;

    public readonly manufacturer: string;

    public readonly manufacturerModelName: string;

    /** Reference to the RT Plan
     * 	(0008,1155) under (300C,0002)
     */
    public readonly referencedRtPlanUid: string;

    /** Referenced Structure Set (Sequence) Attribute
     * (300C,0060), (0008,1155)
     */
    public readonly referencedRtStructureSetUid: string | undefined;

    /** Frame of Reference UID
     * (0020,0052)
     */
    public readonly frameOfReferenceUid: string;

    /**
     * (3004,0002) Units used to describe dose (GY, RELATIVE). https://dicom.innolitics.com/ciods/rt-dose/rt-dose/30040002
     */
    public readonly doseUnits: DoseUnit;

    /**
     * (3004,0004) Type of dose (PHYSICAL, EFFECTIVE). https://dicom.innolitics.com/ciods/rt-dose/rt-dose/30040004
     */
    public readonly doseType: DoseType;

    /**
     * (3004,000a) Dose Summation Type (PLAN, FRACTION, BEAM, BRACHY,...). https://dicom.innolitics.com/ciods/rt-dose/rt-dose/3004000a
     */
    public readonly doseSummationType: DoseSummationType;

    /**
     * 	(3004,0006) Dose Comment Attribute
     *  User-defined comments for dose data.
     */
    public readonly doseComment: string;

    /**
     * Number of pixels in image's vertical direction.\
     * This is the {@link DicomDose image}'s pixel size along y-axis.\
     * (0028, 0010) https://dicom.innolitics.com/ciods/rt-dose/image-pixel/00280010
     */
    public readonly rows: number;

    /**
     * Number of pixels in image's horizontal direction.\
     * This is the {@link DicomDose image}'s pixel size along x-axis.\
     * (0028, 0011) https://dicom.innolitics.com/ciods/rt-dose/image-pixel/00280011
     */
    public readonly cols: number;

    /**
     * Number of planes in dose's z direction.\
     * This is the {@link DicomDose image}'s pixel size along z-axis.\
     * (0028,0008) https://dicom.innolitics.com/ciods/rt-dose/multi-frame/00280008
     */
    public readonly frames: number;

    /**
     * (3004,000E) Dose Grid Scaling Attribute
     * Scaling factor that when multiplied by the dose grid data found in Pixel Data (7FE0,0010) Attribute of the Image Pixel Module, yields grid doses in the dose units as specified by Dose Units
     */
    public readonly doseGridScaling: number;

    /**
     * (0018,1020) Software Versions Attribute
     * Identifies the version of the software that produced the image.
     */

    public readonly softwareVersions: string;

    /**
     * The converted dose matrix data from (7FE0,0010) Pixel Data Attribute in raw internal data format
     */
    private _pixelData?: Uint32Array;
    public get pixelData(): Uint32Array {
        if (!this._pixelData) {
            this._pixelData = this.toInternalPixelData();
        }
        return this._pixelData;
    }
    /**
     * Get the actual presentable pixel values converted into the {@link DoseUnit} of the dose matrix
     */
    public get realPixels(): Float32Array {
        return Float32Array.from(
            this.pixelData,
            (value) => value * this.doseGridScaling
        );
    }

    public readonly dvhSequence: DicomDvh[];

    /**
     * When true, frame order in pixel data must be reversed during
     * internal conversion. This happens when the DICOM
     * GridFrameOffsetVector has a negative step — the origin is
     * adjusted to the last frame but the raw pixel data still has
     * the original (reversed) order.
     */
    private readonly _reverseFrameOrder: boolean;

    constructor(
        sopUid: string,
        frameOfReferenceUid: string,
        pixelSpacing: vec2,
        sliceResolution: number,
        imagePosition: vec3,
        imageOrientation: mat3,
        seriesDescription: string,
        manufacturer: string,
        manufacturerModelName: string,
        rows: number,
        cols: number,
        frames: number,
        doseUnits: DoseUnit,
        doseType: DoseType,
        doseSummationType: DoseSummationType,
        doseComment: string,
        doseGridScaling: number,
        softwareVersions: string,
        referencedRtPlanUid: string,
        referencedRtStructureSetUid: string | undefined,
        dvhSequence: DicomDvh[],
        dataset: DcmJsDatasetWrapper,
        reverseFrameOrder: boolean = false,
        seriesDate?: string,
        seriesTime?: string
    ) {
        super(sopUid, Modality.RTDOSE, dataset);
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.pixelSpacing = pixelSpacing;
        this.sliceResolution = sliceResolution;
        this.imagePosition = imagePosition;
        this.imageOrientation = imageOrientation;
        this.seriesDescription = seriesDescription;
        this.manufacturer = manufacturer;
        this.manufacturerModelName = manufacturerModelName;
        this.rows = rows;
        this.cols = cols;
        this.frames = frames;
        this.doseUnits = doseUnits;
        this.doseType = doseType;
        this.doseSummationType = doseSummationType;
        this.doseComment = doseComment;
        this.doseGridScaling = doseGridScaling;
        this.softwareVersions = softwareVersions;
        this.referencedRtPlanUid = referencedRtPlanUid;
        this.referencedRtStructureSetUid = referencedRtStructureSetUid;
        this.dvhSequence = dvhSequence;
        this._reverseFrameOrder = reverseFrameOrder;
        this.seriesDate = seriesDate;
        this.seriesTime = seriesTime;
    }

    /**
     * Convert the raw pixel data from the DICOM file into an internal format
     */
    private toInternalPixelData(): Uint32Array {
        const pixels = dicomDosePixelDataToInternal(this.datasetWrapper);

        if (this._reverseFrameOrder) {
            const voxelsPerFrame = this.rows * this.cols;
            const reversed = new Uint32Array(pixels.length);
            for (let f = 0; f < this.frames; f++) {
                const srcOffset = f * voxelsPerFrame;
                const destOffset = (this.frames - 1 - f) * voxelsPerFrame;
                reversed.set(
                    pixels.subarray(srcOffset, srcOffset + voxelsPerFrame),
                    destOffset
                );
            }
            return reversed;
        }

        return pixels;
    }
}

export function strToDoseUnit(val: string): DoseUnit {
    const e = DoseUnit[val as keyof typeof DoseUnit];
    if (e === undefined) {
        throw new Error(`Unknown Dose Unit in conversion ${val}`);
    }
    return DoseUnit[e];
}

export function strToDoseType(val: string): DoseType {
    const e = DoseType[val as keyof typeof DoseType];
    if (e === undefined) {
        throw new Error(`Unknown Dose Type in conversion ${val}`);
    }
    return DoseType[e];
}

export function strToDvhType(val: string): DVHType {
    const e = DVHType[val as keyof typeof DVHType];
    if (e === undefined) {
        throw new Error(`Unknown DVH Type in conversion ${val}`);
    }
    return DVHType[e];
}

export function strToVolumeUnits(val: string): DVHVolumeUnits {
    const e = DVHVolumeUnits[val as keyof typeof DVHVolumeUnits];
    if (e === undefined) {
        throw new Error(`Unknown DVHVolumeUnits in conversion ${val}`);
    }
    return DVHVolumeUnits[e];
}

export function strToDoseSummationType(val: string): DoseSummationType {
    const e = DoseSummationType[val as keyof typeof DoseSummationType];
    if (e === undefined) {
        throw new Error(`Unknown Dose Summation Type in conversion ${val}`);
    }
    return DoseSummationType[e];
}
